// B3 (brief-schedule-by-person, ruling F, 2026-11-05) — which layout the
// Schedule's week opens in.
//
// 1. This person's own last choice, kept in this browser under
//    `rosterem.scheduleLayout.<orgId>` (one per organisation, so a person in
//    two organisations keeps two choices).
// 2. Else the organisation's default, `organisation.scheduleLayout`, set in
//    Settings → Global settings by a Manager or Systems Administrator.
// 3. Else By area.
//
// What the stored choice is NOT: an account setting. It lives in one browser
// and goes when the browser's storage is cleared; another device opens at the
// organisation default. Storage that throws (private mode, a blocked or full
// store) is treated as empty and a write that throws is dropped, so the
// Schedule always opens.

export const SCHEDULE_LAYOUTS = Object.freeze(['area', 'person']);
export const DEFAULT_SCHEDULE_LAYOUT = 'area';

export const isScheduleLayout = (v) => typeof v === 'string' && SCHEDULE_LAYOUTS.includes(v);

// The organisation default from an org document (or anything shaped like one).
export const orgScheduleLayout = (org) =>
  (org && isScheduleLayout(org.scheduleLayout) ? org.scheduleLayout : DEFAULT_SCHEDULE_LAYOUT);

const usableOrgId = (orgId) => typeof orgId === 'string' && orgId.trim() !== '';

export const layoutStorageKey = (orgId) => `rosterem.scheduleLayout.${orgId}`;

const defaultStorage = () => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null; // reading window.localStorage itself can throw
  }
};

// This person's stored choice, or null (none, unreadable, or not a layout).
export function readStoredLayout(orgId, storage = defaultStorage()) {
  if (!usableOrgId(orgId) || !storage) return null;
  try {
    const v = storage.getItem(layoutStorageKey(orgId));
    return isScheduleLayout(v) ? v : null;
  } catch {
    return null;
  }
}

// Keeps the choice; returns whether it was kept.
export function writeStoredLayout(orgId, layout, storage = defaultStorage()) {
  if (!usableOrgId(orgId) || !isScheduleLayout(layout) || !storage) return false;
  try {
    storage.setItem(layoutStorageKey(orgId), layout);
    return true;
  } catch {
    return false;
  }
}

export function initialScheduleLayout({ orgId, orgDefault, storage = defaultStorage() } = {}) {
  const stored = readStoredLayout(orgId, storage);
  if (stored) return stored;
  return isScheduleLayout(orgDefault) ? orgDefault : DEFAULT_SCHEDULE_LAYOUT;
}
